import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PromotionServices } from "../services/PromotionServices";

/* ─────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────── */
const API_BASE = "https://api.ifjona.com";

const CARD_PALETTES = [
  {
    bg: "linear-gradient(145deg, #EAF2FF 0%, #D4E7FF 100%)",
    solid: "#D4E7FF",
    accent: "#1455C0",
    pill: "#C2D8FC",
    pillText: "#0D3D8F",
    shadow: "rgba(20,85,192,0.18)",
  },
  {
    bg: "linear-gradient(145deg, #E9F6EE 0%, #CFEADB 100%)",
    solid: "#CFEADB",
    accent: "#0B6E3E",
    pill: "#B8E2CC",
    pillText: "#07502C",
    shadow: "rgba(11,110,62,0.16)",
  },
  {
    bg: "linear-gradient(145deg, #FFF3E8 0%, #FFE4CB 100%)",
    solid: "#FFE4CB",
    accent: "#C25700",
    pill: "#FFCFA5",
    pillText: "#8F3D00",
    shadow: "rgba(194,87,0,0.16)",
  },
  {
    bg: "linear-gradient(145deg, #F4EEFF 0%, #E9DCFF 100%)",
    solid: "#E9DCFF",
    accent: "#5C25CC",
    pill: "#DDD0FF",
    pillText: "#3D1499",
    shadow: "rgba(92,37,204,0.16)",
  },
];

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const getProductImageUrl = (product) => {
  if (!product) return null;
  if (product.image_url) return product.image_url;
  if (product.image) {
    const img = String(product.image);
    return img.startsWith("http") ? img : `${API_BASE}/storage/${img}`;
  }
  return null;
};

const getVideoUrl = (promotion) => {
  if (!promotion) return null;
  if (promotion.video_url) {
    const v = String(promotion.video_url);
    if (v.startsWith("http")) return encodeURI(decodeURI(v));
  }
  if (promotion.video) {
    const path = String(promotion.video);
    const full = path.startsWith("http")
      ? path
      : `${API_BASE}/storage/${path.replace(/^\/+/, "")}`;
    return encodeURI(decodeURI(full));
  }
  return null;
};

/* ─────────────────────────────────────────────
   SKELETON CARD
───────────────────────────────────────────── */
const SkeletonCard = ({ tall }) => (
  <div
    style={{
      borderRadius: 24,
      overflow: "hidden",
      height: tall ? 520 : 420,
      background:
        "linear-gradient(90deg, #f0eff4 25%, #e4e2eb 50%, #f0eff4 75%)",
      backgroundSize: "200% 100%",
      animation: "promoShimmer 1.6s infinite linear",
    }}
  />
);

/* ─────────────────────────────────────────────
   VIDEO BACKGROUND — Edge-safe implementation
───────────────────────────────────────────── */
const VideoBackground = ({ src, poster }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return;
    el.muted = true;
    const p = el.play();
    if (p !== undefined) {
      p.catch(() => {
        // Autoplay silently blocked — video still visible as paused frame
      });
    }
  }, [src]);

  if (failed || !src) return null;

  return (
    <video
      ref={ref}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={poster || undefined}
      webkit-playsinline="true"
      x5-playsinline="true"
      onError={() => setFailed(true)}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        zIndex: 0,
        pointerEvents: "none",
        display: "block",
      }}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

/* ─────────────────────────────────────────────
   PROMO CARD
───────────────────────────────────────────── */
const PromoCard = ({
  title,
  subtitle,
  imageSrc,
  videoSrc,
  palette,
  tall,
  single,
}) => {
  const [hovered, setHovered] = useState(false);
  const hasVideo = Boolean(videoSrc);
  const cardHeight = tall ? 520 : 420;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        borderRadius: 24,
        overflow: "hidden",
        height: cardHeight,
        background: hasVideo ? "#0c0c0e" : palette.bg,
        boxShadow: hovered
          ? `0 36px 72px ${palette.shadow}, 0 10px 28px rgba(0,0,0,0.12)`
          : `0 8px 32px ${palette.shadow}, 0 2px 10px rgba(0,0,0,0.06)`,
        transform: hovered
          ? "translateY(-7px) scale(1.006)"
          : "translateY(0) scale(1)",
        transition:
          "box-shadow 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)",
        cursor: "pointer",
        willChange: "transform",
        isolation: "isolate",
      }}
    >
      {hasVideo && (
        <VideoBackground src={videoSrc} poster={imageSrc || undefined} />
      )}

      {!hasVideo && imageSrc && (
        <img
          src={imageSrc}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: single ? "5%" : "-2%",
            bottom: 0,
            width: single ? "34%" : "50%",
            maxWidth: single ? 500 : 300,
            height: "90%",
            objectFit: "contain",
            zIndex: 1,
            filter: "drop-shadow(0 28px 44px rgba(0,0,0,0.14))",
            transform: hovered
              ? "scale(1.05) translateY(-7px)"
              : "scale(1) translateY(0)",
            transition: "transform 0.44s cubic-bezier(0.22,1,0.36,1)",
            userSelect: "none",
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          background: hasVideo
            ? "linear-gradient(155deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.48) 52%, rgba(0,0,0,0.12) 100%)"
            : `linear-gradient(110deg, ${palette.solid}F5 0%, ${palette.solid}D0 32%, ${palette.solid}30 70%, transparent 100%)`,
          pointerEvents: "none",
        }}
      />

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          opacity: 0.025,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "160px",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: single ? "44px 52px" : "34px 38px",
        }}
      >
        {subtitle && (
          <span
            style={{
              display: "inline-flex",
              alignSelf: "flex-start",
              alignItems: "center",
              marginBottom: 14,
              padding: "5px 14px",
              borderRadius: 100,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              background: hasVideo
                ? "rgba(255,255,255,0.14)"
                : palette.pill,
              color: hasVideo
                ? "rgba(255,255,255,0.9)"
                : palette.pillText,
              backdropFilter: hasVideo ? "blur(10px)" : "none",
              WebkitBackdropFilter: hasVideo ? "blur(10px)" : "none",
              border: hasVideo
                ? "1px solid rgba(255,255,255,0.2)"
                : "none",
            }}
          >
            {subtitle}
          </span>
        )}

        <h2
          style={{
            margin: "0 0 22px",
            fontFamily:
              "'Playfair Display', 'Cormorant Garamond', 'Georgia', serif",
            fontSize: single
              ? "clamp(28px, 3.4vw, 50px)"
              : "clamp(20px, 2.4vw, 34px)",
            fontWeight: 700,
            lineHeight: 1.16,
            letterSpacing: "-0.028em",
            color: hasVideo ? "#ffffff" : "#0e0e12",
            maxWidth: single ? "52%" : hasVideo ? "70%" : "56%",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textShadow: hasVideo
              ? "0 2px 20px rgba(0,0,0,0.38)"
              : "none",
          }}
        >
          {title}
        </h2>

        <Link
          to="/shop"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            alignSelf: "flex-start",
            padding: "13px 28px",
            borderRadius: 100,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            textDecoration: "none",
            background: hasVideo ? "#ffffff" : palette.accent,
            color: hasVideo ? palette.accent : "#ffffff",
            boxShadow: hasVideo
              ? "0 4px 20px rgba(0,0,0,0.24)"
              : `0 6px 20px ${palette.shadow}`,
            transition:
              "gap 0.24s ease, transform 0.24s cubic-bezier(0.22,1,0.36,1), box-shadow 0.24s ease",
            willChange: "transform",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.gap = "16px";
            e.currentTarget.style.transform = "scale(1.04)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.gap = "10px";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          Shop Now
          <svg
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 6.5h9M8 3l3.5 3.5L8 10"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </article>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const PromotionalOne = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const list = await PromotionServices.getPromotions();
        setPromotions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Promotion load error:", e);
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const bannerItems = useMemo(
    () =>
      (promotions || [])
        .map((p, index) => {
          const product = p?.product;
          return {
            id: p?.id ?? index,
            title:
              product?.title ||
              product?.name ||
              p?.title ||
              `Limited Offer ${index + 1}`,
            subtitle:
              p?.badge_label ||
              product?.category?.name ||
              product?.brand ||
              null,
            imageSrc: getProductImageUrl(product),
            videoSrc: getVideoUrl(p),
            palette: CARD_PALETTES[index % CARD_PALETTES.length],
          };
        })
        .slice(0, 4),
    [promotions]
  );

  const isSingle = bannerItems.length === 1;

  if (!loading && bannerItems.length === 0) {
    return null;
  }

  return (
    <section style={{ padding: "72px 0 48px", contain: "layout style" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap');

        @keyframes promoShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .promo-section-grid {
          display: grid;
          gap: 24px;
          grid-template-columns: ${isSingle ? "1fr" : "repeat(2, 1fr)"};
        }

        @media (max-width: 960px) {
          .promo-section-grid { grid-template-columns: 1fr; gap: 18px; }
        }

        @media (max-width: 600px) {
          .promo-section-grid { gap: 14px; }
        }

        video::-webkit-media-controls,
        video::-webkit-media-controls-enclosure,
        video::-webkit-media-controls-panel { display: none !important; }
      `}</style>

      <div
        style={{
          maxWidth: 1340,
          marginInline: "auto",
          paddingInline: "clamp(16px, 3vw, 40px)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "grid",
              gap: 24,
              gridTemplateColumns: "repeat(2, 1fr)",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <SkeletonCard key={i} tall={i % 2 === 0} />
            ))}
          </div>
        ) : (
          <div className="promo-section-grid">
            {bannerItems.map((item, index) => (
              <PromoCard
                key={item.id}
                {...item}
                tall={index % 2 === 0}
                single={isSingle}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PromotionalOne;